import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { Flex, Text } from 'core/components';
import BoardModel from 'app/stores/boards/BoardModel';

/**
 * Title + description area that sits above the board grid.
 *
 * The header scrolls with the rest of the board content (it is rendered
 * inside the scroll container above the sticky toolbar), so scrolling will
 * naturally move it under the sticky filters/time bar.
 */

const HeaderRow = styled(Flex)`
  padding: 16px 12px 0px 4px;
  align-items: flex-start;
  gap: 8px;
`;

const TitleInput = styled.input`
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  color: inherit;
  font-size: 20px;
  font-weight: 400;
  padding: 8px 12px;
  outline: none;
  min-width: 60px;
  max-width: 75%;
  width: 100%;
  margin-top: -8px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.cardBorder};
  }
  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const TitleText = styled.span`
  font-size: 20px;
  font-weight: 400;
  padding: 4px 8px;
  display: inline-block;
`;

const DescriptionInput = styled.input`
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 14px;
  padding: 4px 12px;
  display: block;
  width: 100%;
  outline: none;
  margin-top: -10px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.cardBorder};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.muted};
    opacity: 0.55;
  }
`;

interface BoardConfig {
  description?: string;
}

interface Props {
  board: BoardModel;
  canEdit: boolean;
  rightSlot?: React.ReactNode;
}

function BoardCollapsibleHeader({ board, canEdit, rightSlot }: Props) {
  const title: string = (board.get('title') as string) || '';
  const config = (board.get('config') as BoardConfig) || {};
  const description: string = config.description || '';

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    board.safeSet({ title: e.target.value });
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    board.safeSet({ config: { ...config, description: e.target.value } });
  };

  const renderDescription = () => {
    if (canEdit) {
      return (
        <DescriptionInput
          value={description}
          onChange={handleDescriptionChange}
          placeholder="Add a description…"
          aria-label="Dashboard description"
        />
      );
    }
    if (description) {
      return <Text color="muted">{description}</Text>;
    }
    return null;
  };

  return (
    <HeaderRow>
      <Flex flexDirection="column" flex={1} gap={1} minWidth={0}>
        <Flex alignItems="center" gap={2}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {canEdit ? (
              <TitleInput
                value={title}
                onChange={handleTitleChange}
                placeholder="Untitled Dashboard"
                aria-label="Dashboard title"
              />
            ) : (
              <TitleText>{title}</TitleText>
            )}
          </div>
          {rightSlot}
        </Flex>
        {renderDescription()}
      </Flex>
    </HeaderRow>
  );
}

export default observer(BoardCollapsibleHeader);
